import { cn } from '@/lib/cn';

/**
 * Label-beside-value rows shared by the profile's read and edit modes, so a
 * field sits in exactly the same place whether you're looking at it or
 * changing it.
 */
export function PropertyGroup({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  return (
    <section className={cn('space-y-1', className)}>
      <div className="flex items-end justify-between gap-3 pb-1">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          {description ? (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          ) : null}
        </div>
        {action}
      </div>
      <dl className="divide-y divide-gray-100 border-y border-gray-100 dark:divide-gray-800 dark:border-gray-800">
        {children}
      </dl>
    </section>
  );
}

const ROW = 'grid grid-cols-1 gap-x-6 gap-y-1 py-2.5 sm:grid-cols-[11rem_minmax(0,1fr)]';

/** Copy shown in place of a field the API withheld from this viewer. */
export const HIDDEN_FIELD_TITLE = "You don't have permission to view this";

/**
 * Read-only row. `null` renders a quiet "Not set" so gaps stay visible but calm;
 * `hidden` renders "Hidden" for a field the API withheld from this viewer.
 */
export function PropertyRow({
  label,
  value,
  hint,
  mono = false,
  hidden = false,
}: {
  label: string;
  value: React.ReactNode | null;
  /** Secondary detail after the value, e.g. an age next to a date of birth. */
  hint?: string | null;
  mono?: boolean;
  hidden?: boolean;
}): React.ReactElement {
  if (hidden) {
    return (
      <div className={cn(ROW, 'sm:items-baseline')}>
        <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
        <dd className="text-sm text-gray-400 dark:text-gray-500" title={HIDDEN_FIELD_TITLE}>
          Hidden
        </dd>
      </div>
    );
  }
  const empty = value == null || value === '';
  return (
    <div className={cn(ROW, 'sm:items-baseline')}>
      <dt className="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          'min-w-0 text-sm wrap-break-word',
          empty ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100',
          mono && !empty && 'tabular-nums'
        )}
      >
        {empty ? 'Not set' : value}
        {!empty && hint ? (
          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{hint}</span>
        ) : null}
      </dd>
    </div>
  );
}

/** Editable row: same grid as {@link PropertyRow}, with the control in the value column. */
export function PropertyField({
  label,
  htmlFor,
  hint,
  error,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={cn(ROW, 'sm:items-start')}>
      <dt className="sm:pt-2">
        {htmlFor ? (
          <label htmlFor={htmlFor} className="text-sm text-gray-600 dark:text-gray-300">
            {label}
          </label>
        ) : (
          <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
        )}
      </dt>
      <dd className="min-w-0">
        {children}
        {error ? (
          <p className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        ) : hint ? (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
        ) : null}
      </dd>
    </div>
  );
}

/** Compact control sizing for {@link PropertyField}; overrides the 44px form default. */
export const FIELD_CONTROL =
  'h-9 min-h-0 rounded-lg px-3 text-sm shadow-none focus:ring-2 focus:ring-primary/15';
